import React, { useRef } from 'react';
import { Box } from '@mui/material';
import { colors, spacing } from '../constants';
import { useAssignedQueriesPageState } from '../hooks/queries/assignedQueries/useAssignedQueriesPageState';
import { useAssignedQueriesPageData } from '../hooks/queries/assignedQueries/useAssignedQueriesPageData';
import { useAssignedQueriesPageFilters } from '../hooks/queries/assignedQueries/useAssignedQueriesPageFilters';
import { useAssignedQueriesPageModals } from '../hooks/queries/assignedQueries/useAssignedQueriesPageModals';
import { useAssignedQueriesPageOperations } from '../hooks/queries/assignedQueries/useAssignedQueriesPageOperations';
import { useAssignedQueriesAssignment } from '../hooks/queries/assignedQueries/useAssignedQueriesAssignment';
import { REGION_OPTIONS } from '../constants/assignedQueriesPage';
import { getVisibleRegionOptions, getDisplayStatus, formatQueryType, getAssignorInfo, shouldShowAssignedByColumn } from '../utils/assignedQueriesUtils';
import SearchAndFilter from '../components/queries/SearchAndFilter';
import ViewQueryModal from '../components/queries/ViewQueryModal';
import EscalateQueryDialog from '../components/queries/EscalateQueryDialog';
import RegionCardsGrid from '../components/raiseQueryPage/RegionCardsGrid';
import AssignedQueriesTable from '../components/assignedQueriesPage/AssignedQueriesTable';
import AssignDialog from '../components/assignedQueriesPage/AssignDialog';

const AssignedQueries = () => {
  const tableContainerRef = useRef(null);

  // Initialize all hooks
  const pageState = useAssignedQueriesPageState();

  const { fetchQueries, projectsCatalog } = useAssignedQueriesPageData({
    searchQuery: pageState.searchQuery,
    filters: pageState.filters,
    currentUser: pageState.currentUser,
    setAllQueries: pageState.setAllQueries,
    setLoading: pageState.setLoading,
    setProjectOptions: pageState.setProjectOptions,
    setAssignedFilterOptions: pageState.setAssignedFilterOptions,
    setQueryTypeOptions: pageState.setQueryTypeOptions,
    setStatusSummary: pageState.setStatusSummary,
    setBackendCounts: pageState.setBackendCounts,
    setCurrentUser: pageState.setCurrentUser,
    setCurrentUserRole: pageState.setCurrentUserRole,
    setCurrentUserRegion: pageState.setCurrentUserRegion,
  });

  const visibleRegionOptions = React.useMemo(() => {
    return getVisibleRegionOptions(
      pageState.currentUserRole,
      pageState.currentUserRegion,
      REGION_OPTIONS
    );
  }, [pageState.currentUserRole, pageState.currentUserRegion]);

  const {
    filteredQueries,
    paginatedQueries,
    groupedQueriesByProject,
    paginatedProjects,
    totalProjects,
  } = useAssignedQueriesPageFilters({
    allQueries: pageState.allQueries,
    selectedRegionCard: pageState.selectedRegionCard,
    selectedStatusCard: pageState.selectedStatusCard,
    tableAssignedFilter: pageState.tableAssignedFilter,
    page: pageState.page,
    rowsPerPage: pageState.rowsPerPage,
    visibleRegionOptions,
    backendCounts: pageState.backendCounts,
    setQueries: pageState.setQueries,
    setTotal: pageState.setTotal,
    setStatusSummary: pageState.setStatusSummary,
    setRegionStatusSummary: pageState.setRegionStatusSummary,
    setPage: pageState.setPage,
  });

  const modals = useAssignedQueriesPageModals();
  const assignment = useAssignedQueriesAssignment(pageState.currentUser, projectsCatalog);
  const operations = useAssignedQueriesPageOperations(
    fetchQueries,
    modals.setEscalatingId,
    modals.setAssigningId,
    modals.handleCloseEscalateDialog,
    modals.handleCloseAssignDialog
  );

  // Fetch ED users by region
  React.useEffect(() => {
    const fetchEdUsers = async () => {
      try {
        const api = (await import('../services/api')).default;
        const response = await api.get('/auth/ed-users-by-region');
        if (response.data?.success && response.data?.data?.edByRegion) {
          pageState.setEdUsersByRegion(response.data.data.edByRegion);
        }
      } catch (error) {
        // console.error('Failed to load ED users:', error);
      }
    };

    fetchEdUsers();
  }, [pageState.setEdUsersByRegion]);

  // Handlers
  const handleRegionCardClick = (regionCode) => {
    if (pageState.selectedRegionCard === regionCode) {
      pageState.setSelectedRegionCard(null);
      pageState.setSelectedStatusCard(null);
    } else {
      pageState.setSelectedRegionCard(regionCode);
      pageState.setSelectedStatusCard(null);
      // Scroll to table after a short delay to ensure state update
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
    pageState.setPage(0);
  };

  const handleStatusCardClick = (statusLabel, regionCode, ref) => {
    const isStatusSelected = pageState.selectedRegionCard === regionCode && pageState.selectedStatusCard === statusLabel;
    if (isStatusSelected) {
      pageState.setSelectedStatusCard(null);
    } else {
      pageState.setSelectedRegionCard(regionCode);
      pageState.setSelectedStatusCard(statusLabel);
      // Scroll to table after a short delay to ensure state update
      setTimeout(() => {
        if (ref?.current) {
          ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
    pageState.setPage(0);
  };

  const handleView = (query) => {
    modals.handleOpenViewModal(query);
  };

  const handleOpenAssignDialog = (query) => {
    modals.handleOpenAssignDialog(query);
    assignment.setSelectedAssigneeId('');
    assignment.setAssignReason(assignment.assignReason || 'Handing off to responder');
    assignment.setAssignNote(assignment.assignNote || 'Awaiting response');
    assignment.loadAssignableUsers(query, query?.region);
  };

  const handleCloseAssignDialog = () => {
    modals.handleCloseAssignDialog();
    assignment.resetAssignDialog();
  };

  const handleConfirmAssign = () => {
    operations.handleConfirmAssign(
      modals.assignTarget,
      assignment.selectedAssigneeId,
      assignment.assignReason,
      assignment.assignNote
    );
  };

  const handleConfirmEscalation = async (escalationData) => {
    await operations.handleConfirmEscalation(modals.escalateTarget, escalationData);
  };

  const shouldShowAssignedBy = React.useMemo(() => {
    return shouldShowAssignedByColumn(pageState.queries, pageState.currentUser);
  }, [pageState.queries, pageState.currentUser]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, p: { xs: spacing.sm, sm: spacing.md } }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        {/* Region Cards Grid */}
        <RegionCardsGrid
          visibleRegionOptions={visibleRegionOptions}
          regionStatusSummary={pageState.regionStatusSummary}
          edUsersByRegion={pageState.edUsersByRegion}
          selectedRegionCard={pageState.selectedRegionCard}
          selectedStatusCard={pageState.selectedStatusCard}
          onRegionCardClick={handleRegionCardClick}
          onStatusCardClick={handleStatusCardClick}
          tableContainerRef={tableContainerRef}
        />

        {/* Search and Filter */}
        <Box sx={{ mb: spacing.md }}>
          <SearchAndFilter
            onSearchChange={pageState.handleSearchChange}
            onFilterChange={pageState.handleFilterChange}
            searchValue={pageState.searchQuery}
            filters={pageState.filters}
            defaultExpanded={false}
            showStatusFilter={false}
            showPriorityFilter={false}
            showProjectFilter={false}
            showLifecycleStatusFilter={false}
            showAssignedFilter={true}
            showQueryTypeFilter={false}
            projectOptions={pageState.projectOptions}
            assignedOptions={pageState.assignedFilterOptions}
            queryTypeOptions={pageState.queryTypeOptions}
            placeholder="Search assigned queries by ID, title, description, or project..."
            onRefresh={fetchQueries}
            refreshTooltip="Refresh assigned queries"
            refreshing={pageState.loading}
          />
        </Box>

        {/* Query Table */}
        <AssignedQueriesTable
          loading={pageState.loading}
          total={pageState.total}
          paginatedProjects={paginatedProjects}
          groupedQueriesByProject={groupedQueriesByProject}
          expandedProjects={pageState.expandedProjects}
          projectsCatalog={projectsCatalog}
          onToggleProject={pageState.toggleProject}
          onView={handleView}
          onAssign={handleOpenAssignDialog}
          page={pageState.page}
          rowsPerPage={pageState.rowsPerPage}
          handleChangePage={pageState.handleChangePage}
          handleChangeRowsPerPage={pageState.handleChangeRowsPerPage}
          getDisplayStatus={getDisplayStatus}
          formatQueryType={formatQueryType}
          getAssignorInfo={getAssignorInfo}
          shouldShowAssignedByColumn={shouldShowAssignedBy}
          currentUser={pageState.currentUser}
          currentUserRole={pageState.currentUserRole}
          assigningId={modals.assigningId}
        />
      </Box>

      {/* Modals */}
      <ViewQueryModal
        open={modals.viewModalOpen}
        onClose={modals.handleCloseViewModal}
        queryId={modals.selectedQuery?._id}
        onDelegated={fetchQueries}
      />
      <EscalateQueryDialog
        open={modals.escalateDialogOpen}
        query={modals.escalateTarget}
        submitting={modals.escalatingId === modals.escalateTarget?._id}
        onClose={modals.handleCloseEscalateDialog}
        onSubmit={handleConfirmEscalation}
      />
      <AssignDialog
        open={modals.assignDialogOpen}
        onClose={handleCloseAssignDialog}
        query={modals.assignTarget}
        assignUsers={assignment.assignUsers}
        selectedAssigneeId={assignment.selectedAssigneeId}
        onAssigneeChange={(e) => assignment.setSelectedAssigneeId(e.target.value)}
        loadingAssignUsers={assignment.loadingAssignUsers}
        assigningId={modals.assigningId}
        onConfirm={handleConfirmAssign}
      />
    </Box>
  );
};

export default AssignedQueries;
